import { SectionSelector } from "@/shared/selectors";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { DelayCategorySelector } from "@/shared/selectors/DelayCategorySelector";
import { DelayCodeTreePicker } from "@/shared/selectors/DelayCodeTreePicker";
import { getMomentTz } from "@dexteel/mesf-core";
import GroupsIcon from "@mui/icons-material/Groups";
import PieChartIcon from "@mui/icons-material/PieChart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Checkbox, Chip, FormControlLabel, Grid2 } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FC } from "react";
import {
	EndFilter,
	StartFilter,
} from "../../../shared/components/StartEndFilter";
import { DelayReportFilters } from "../../DelaysReportPage";
import { CrewsFilter } from "./CrewsFilter";
import { ProductionEventSelector } from "./ProductionEventSelector";
import { ShiftsFilter } from "./ShiftsFilter";

const moment = getMomentTz();

export type ViewType = "summary" | "trend" | "category" | "crewShift";

const VIEW_OPTIONS: {
	value: ViewType;
	label: string;
	icon: React.ReactNode;
}[] = [
	{
		value: "summary",
		label: "Summary",
		icon: <SummarizeIcon fontSize="small" />,
	},
	{
		value: "trend",
		label: "Daily Trend",
		icon: <TrendingUpIcon fontSize="small" />,
	},
	{
		value: "category",
		label: "By Category",
		icon: <PieChartIcon fontSize="small" />,
	},
	{
		value: "crewShift",
		label: "By Crew & Shift",
		icon: <GroupsIcon fontSize="small" />,
	},
];

export const ViewChip = ({
	label,
	icon,
	selected,
	onClick,
}: {
	label: string;
	icon: React.ReactNode;
	selected: boolean;
	onClick: () => void;
}) => {
	return (
		<Chip
			icon={<Box sx={{ display: "flex", ml: 0.5 }}>{icon}</Box>}
			label={label}
			onClick={onClick}
			sx={{
				borderRadius: 1,
				fontWeight: selected ? 600 : 400,
				fontSize: "0.8rem",
				height: 32,
				transition: "all 0.15s ease",
				border: (theme) =>
					`1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
				backgroundColor: (theme) =>
					selected ? alpha(theme.palette.primary.main, 0.1) : "transparent",
				color: (theme) =>
					selected ? theme.palette.primary.main : theme.palette.text.secondary,
				"& .MuiChip-icon": {
					color: (theme) =>
						selected
							? theme.palette.primary.main
							: theme.palette.text.secondary,
				},
				"&:hover": {
					backgroundColor: (theme) =>
						selected
							? alpha(theme.palette.primary.main, 0.18)
							: alpha(theme.palette.text.primary, 0.05),
					borderColor: (theme) =>
						selected
							? theme.palette.primary.main
							: theme.palette.text.secondary,
				},
			}}
			data-test-id={`filter-mode-${label.toLowerCase()}`}
		/>
	);
};

type Props = {
	filters: DelayReportFilters;
	onFilterChange: (updates: Partial<DelayReportFilters>) => void;
	mainMode: "table" | "views";
	selectedView: ViewType;
	setSelectedView: (view: ViewType) => void;
};

export const Filters: FC<Props> = ({
	filters,
	onFilterChange,
	mainMode,
	selectedView,
	setSelectedView,
}) => {
	return (
		<Grid2 container style={{ alignItems: "center" }} spacing={1}>
			<Grid2 size={{ xs: 12, md: 2 }}>
				<StartFilter
					value={filters.startDate}
					onChange={(date) => date && onFilterChange({ startDate: date })}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 2 }}>
				<EndFilter
					value={filters.endDate}
					onChange={(date) => date && onFilterChange({ endDate: date })}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 2 }}>
				<DelayAreaSelector
					value={filters.delayAreaId}
					onChange={(delayAreaId: number | null) =>
						onFilterChange({
							delayAreaId,
							sectionId: null,
							productionEventId: null,
							productionEventMillAssetId: null,
						})
					}
					isFiltering={mainMode === "table"}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<DelayCategorySelector
					label="Category"
					assetId={filters.delayAreaId}
					onChange={(delayCategoryId: number | null) =>
						onFilterChange({ delayCategoryId })
					}
					value={filters.delayCategoryId}
					disabled={mainMode === "views"}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 3 }}>
				<DelayCodeTreePicker
					assetId={filters.delayAreaId}
					onChange={(delayCodeId) =>
						onFilterChange({ delayCodeId: delayCodeId || null })
					}
					value={filters.delayCodeId}
					disabled={mainMode === "views"}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 2 }}>
				<SectionSelector
					value={filters.sectionId}
					assetId={filters.delayAreaId ?? undefined}
					sortBy="recent"
					hideTph
					onChange={(sectionId) =>
						onFilterChange({
							sectionId,
							productionEventId: null,
							productionEventMillAssetId: null,
						})
					}
				/>
			</Grid2>
			{filters.sectionId !== null ? (
				<Grid2 size={{ xs: 12, md: 3.5 }}>
					<ProductionEventSelector
						sectionId={filters.sectionId}
						delayAreaAssetId={filters.delayAreaId}
						value={filters.productionEventId}
						millAssetId={filters.productionEventMillAssetId}
						onChange={(productionEvent) => {
							if (!productionEvent) {
								onFilterChange({
									productionEventId: null,
									productionEventMillAssetId: null,
								});
								return;
							}

							if (!productionEvent.End) {
								onFilterChange({
									productionEventId: productionEvent.EventId,
									productionEventMillAssetId: productionEvent.MillAssetId,
								});
								return;
							}

							onFilterChange({
								productionEventId: productionEvent.EventId,
								productionEventMillAssetId: productionEvent.MillAssetId,
								startDate: moment(productionEvent.Start).startOf("day"),
								endDate: moment(productionEvent.End).endOf("day"),
							});
						}}
					/>
				</Grid2>
			) : null}
			{mainMode == "table" ? (
				<>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<ShiftsFilter
							value={filters.shift}
							onChange={(shift) => onFilterChange({ shift })}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<CrewsFilter
							value={filters.crew as string}
							onChange={(crew) => onFilterChange({ crew })}
						/>
					</Grid2>
				</>
			) : null}
			<Grid2 size={{ xs: 12, md: 2 }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={filters.includeMicrodelays}
							onChange={(e) =>
								onFilterChange({ includeMicrodelays: e.target.checked })
							}
						/>
					}
					label="Microdelays"
					style={{ marginBottom: 0, marginLeft: 1, width: "100%" }}
				/>
			</Grid2>
			{mainMode === "views" && (
				<Grid2 size={{ xs: 12 }}>
					<Box
						sx={{
							display: "flex",
							gap: 1,
							py: 1,
							flexWrap: "wrap",
						}}
					>
						{VIEW_OPTIONS.map((option) => (
							<ViewChip
								key={option.value}
								label={option.label}
								icon={option.icon}
								selected={selectedView === option.value}
								onClick={() => setSelectedView(option.value)}
							/>
						))}
					</Box>
				</Grid2>
			)}
		</Grid2>
	);
};
